/**
 * The texture manifest: the file that says which image belongs in which material slot when a
 * character was exported WITHOUT its textures.
 *
 * It lives in `src/shared` because both ends need it and neither may reach the other: the editor
 * writes it from a source GLB (`src/importExport/textureManifest.ts`) and the runtime SDK reads it
 * to attach the images at play time (`src/runtime/textures.ts`), and `src/runtime` must never
 * import from `src/importExport` (a game should not pull an editor into its bundle).
 *
 * Same rule as the library schema: never accept a partially corrupt file silently. Unlike the
 * library schema, a broken entry here costs a texture, not a user's animation work, so
 * `parseTextureManifest` drops what it cannot use and REPORTS it rather than rejecting the whole
 * file — a character with three of its four maps is a better outcome than a character with none.
 */

/** Manifest format version. Bump only for a change a reader could not safely ignore. */
export const TEXTURE_MANIFEST_SCHEMA_VERSION = 1;

/**
 * `material.userData` key holding the SOURCE glTF material index. `exportGlb` writes it onto the
 * stripped material clones, `GLTFExporter` turns it into glTF `extras`, `GLTFLoader` reads it back
 * into `userData`, and `applyTextures` matches on it.
 *
 * An index rather than a name because glTF does not require material names to be unique or even
 * present, and this project already carries a scar from trusting names in exactly this way
 * (`.claude/vault/glb-export-bone-names.md`). The name travels alongside as the fallback.
 */
export const MATERIAL_KEY = 'animstatexMaterial';

/**
 * The three.js material slots a manifest can fill.
 *
 * The core PBR six come first; the rest are the `KHR_materials_*` extension maps. The extension
 * ones are here rather than left out because the STRIPPER enumerates a material's textures instead
 * of consulting a list (a fixed list that misses one slot ships an image in a file that claims to
 * have none). Once a texture is guaranteed to be stripped, refusing to carry it in the manifest
 * would turn "export without textures" into "silently lose the clearcoat mask" for any character
 * that uses one.
 */
export type TextureSlot =
  | 'map'
  | 'normalMap'
  | 'roughnessMap'
  | 'metalnessMap'
  | 'aoMap'
  | 'emissiveMap'
  | 'clearcoatMap'
  | 'clearcoatRoughnessMap'
  | 'clearcoatNormalMap'
  | 'sheenColorMap'
  | 'sheenRoughnessMap'
  | 'transmissionMap'
  | 'thicknessMap'
  | 'iridescenceMap'
  | 'iridescenceThicknessMap'
  | 'anisotropyMap'
  | 'specularIntensityMap'
  | 'specularColorMap';

export const TEXTURE_SLOTS: readonly TextureSlot[] = [
  'map',
  'normalMap',
  'roughnessMap',
  'metalnessMap',
  'aoMap',
  'emissiveMap',
  'clearcoatMap',
  'clearcoatRoughnessMap',
  'clearcoatNormalMap',
  'sheenColorMap',
  'sheenRoughnessMap',
  'transmissionMap',
  'thicknessMap',
  'iridescenceMap',
  'iridescenceThicknessMap',
  'anisotropyMap',
  'specularIntensityMap',
  'specularColorMap',
];

/** One extracted image file: the name it is saved as and fetched by. */
export interface ManifestImage {
  file: string;
  mimeType: string;
  bytes: number;
  /** Index in the source glTF `images` array — diagnostic, nothing matches on it. */
  sourceIndex: number;
}

/** `KHR_texture_transform`, already translated into three's vocabulary. */
export interface ManifestTextureTransform {
  offset?: [number, number];
  /** glTF calls this `scale`; three calls it `repeat`. Renamed at extraction, not at load. */
  repeat?: [number, number];
  rotation?: number;
}

export interface ManifestSlot {
  /** Index into `TextureManifest.images`. */
  image: number;
  /**
   * `'srgb'` for colour data (base colour, emissive), `'linear'` for data maps (normal, ORM).
   * Getting this wrong is the classic washed-out or too-dark texture, so it is stated per slot
   * rather than re-derived by whoever reads the file.
   */
  colorSpace: 'srgb' | 'linear';
  /** Which UV set: `0` -> the `uv` attribute, `1` -> `uv1`. three reads it as `texture.channel`. */
  texCoord: number;
  /**
   * glTF sampler constants — the WebGL enum values (`10497` REPEAT, `9729` LINEAR…), as they were
   * in the source file. three uses its OWN numbering for these, so a reader must translate;
   * `applyTextures` carries the same tables `GLTFLoader` does. Passing them through raw sets a
   * nonsense wrap mode.
   */
  wrapS?: number;
  wrapT?: number;
  magFilter?: number;
  minFilter?: number;
  transform?: ManifestTextureTransform;
  /** `normalTexture.scale` -> `normalScale`, `occlusionTexture.strength` -> `aoMapIntensity`. */
  scale?: number;
}

/**
 * Scalar material values that had to be NEUTRALIZED in the stripped GLB, and their real values
 * for `applyTextures` to restore.
 *
 * This exists because of a three.js default that turns the feature on its head: `GLTFLoader` sets
 * `metalness` and `roughness` to **1.0** when the glTF omits `metallicFactor`/`roughnessFactor`
 * (`GLTFLoader.js:3586-3587`), which is exactly what an exporter writes when those values live in
 * the metallic-roughness TEXTURE. Strip that texture and keep the factors, and a perfectly ordinary
 * character loads as a fully metallic, fully smooth black mirror — worse than useless as the
 * "show it now, texture it later" placeholder this whole feature is for. Same story for a masked
 * `emissiveFactor`: glowing eyes become a uniformly white-hot silhouette.
 *
 * So the stripped GLB gets matte, non-emissive placeholder values for exactly the channels whose
 * texture was removed, and the truth is recorded here. A channel whose texture was NOT stripped
 * keeps its authored factor and never appears in this block.
 */
export interface ManifestFactors {
  metalness?: number;
  roughness?: number;
  /** Linear RGB, as `MeshStandardMaterial.emissive` holds it. */
  emissive?: [number, number, number];
}

export interface ManifestMaterial {
  /** Source glTF material index; matches `userData[MATERIAL_KEY]` in the exported GLB. */
  id: number;
  /** The material's name, for humans and as the fallback match. May be empty. */
  name: string;
  slots: Partial<Record<TextureSlot, ManifestSlot>>;
  /** Present only when the stripped GLB carries placeholder values that must be put back. */
  factors?: ManifestFactors;
}

export interface TextureManifest {
  schemaVersion: number;
  generator: string;
  /** File name of the GLB these textures belong to. Informational; nothing keys off it. */
  model: string;
  /**
   * glTF images are authored with the origin at the TOP left, so a loader must not flip them.
   * Stated in the file because someone will eventually supply their own images and wonder.
   */
  flipY: false;
  images: ManifestImage[];
  materials: ManifestMaterial[];
  warnings: string[];
}

export interface TextureManifestParseResult {
  manifest: TextureManifest | null;
  /** Fatal: the file is not a manifest at all. `manifest` is `null`. */
  errors: string[];
  /** Non-fatal: entries that were dropped. `manifest` holds everything that survived. */
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Serialization
// ---------------------------------------------------------------------------

/** Pretty, like every other file this project writes by hand. Trailing newline included. */
export function textureManifestToJson(manifest: TextureManifest): string {
  return `${JSON.stringify(manifest, null, 2)}\n`;
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function finiteNumber(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function pair(v: unknown): [number, number] | undefined {
  if (!Array.isArray(v) || v.length < 2) return undefined;
  const a = finiteNumber(v[0]);
  const b = finiteNumber(v[1]);
  return a === undefined || b === undefined ? undefined : [a, b];
}

function parseSlot(
  raw: unknown,
  imageCount: number,
  label: string,
  warnings: string[],
): ManifestSlot | null {
  if (!isPlainObject(raw)) {
    warnings.push(`${label}: not an object; dropped.`);
    return null;
  }
  const image = finiteNumber(raw.image);
  if (image === undefined || !Number.isInteger(image) || image < 0 || image >= imageCount) {
    warnings.push(`${label}: image index ${String(raw.image)} does not exist; dropped.`);
    return null;
  }

  const slot: ManifestSlot = {
    image,
    // Anything that is not the literal 'srgb' is treated as linear. Linear is the safer default:
    // a colour map read as linear looks washed out, a data map read as sRGB is geometrically
    // wrong (a normal map decoded through a gamma curve produces bent lighting).
    colorSpace: raw.colorSpace === 'srgb' ? 'srgb' : 'linear',
    texCoord: finiteNumber(raw.texCoord) ?? 0,
  };

  for (const key of ['wrapS', 'wrapT', 'magFilter', 'minFilter', 'scale'] as const) {
    const value = finiteNumber(raw[key]);
    if (value !== undefined) slot[key] = value;
  }

  if (isPlainObject(raw.transform)) {
    const transform: ManifestTextureTransform = {};
    const offset = pair(raw.transform.offset);
    const repeat = pair(raw.transform.repeat);
    const rotation = finiteNumber(raw.transform.rotation);
    if (offset) transform.offset = offset;
    if (repeat) transform.repeat = repeat;
    if (rotation !== undefined) transform.rotation = rotation;
    if (Object.keys(transform).length > 0) slot.transform = transform;
  }

  return slot;
}

function parseFactors(raw: unknown): ManifestFactors | undefined {
  if (!isPlainObject(raw)) return undefined;
  const factors: ManifestFactors = {};
  const metalness = finiteNumber(raw.metalness);
  const roughness = finiteNumber(raw.roughness);
  if (metalness !== undefined) factors.metalness = metalness;
  if (roughness !== undefined) factors.roughness = roughness;
  if (Array.isArray(raw.emissive) && raw.emissive.length >= 3) {
    const rgb = raw.emissive.slice(0, 3).map((c) => finiteNumber(c));
    if (rgb.every((c): c is number => c !== undefined)) {
      factors.emissive = [rgb[0], rgb[1], rgb[2]];
    }
  }
  return Object.keys(factors).length > 0 ? factors : undefined;
}

/**
 * Parse manifest JSON text. `errors` non-empty means the file is not usable at all; `warnings`
 * lists entries that were dropped while the rest survived.
 *
 * A manifest from a NEWER schema version is not rejected: every field this reader knows is still
 * where it was, and refusing to texture a character because the file also carries something new
 * would be a worse failure than ignoring the new part. It is reported as a warning.
 */
export function parseTextureManifest(text: string): TextureManifestParseResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (error) {
    return {
      manifest: null,
      errors: [`The texture manifest is not valid JSON: ${(error as Error).message}`],
      warnings,
    };
  }

  if (!isPlainObject(raw)) {
    return { manifest: null, errors: ['The texture manifest is not a JSON object.'], warnings };
  }

  const schemaVersion = finiteNumber(raw.schemaVersion);
  if (schemaVersion === undefined) {
    errors.push('The texture manifest has no schemaVersion.');
  } else if (schemaVersion > TEXTURE_MANIFEST_SCHEMA_VERSION) {
    warnings.push(
      `The manifest is schema version ${schemaVersion}; this build understands ` +
        `${TEXTURE_MANIFEST_SCHEMA_VERSION}. Unknown fields are ignored.`,
    );
  }

  if (!Array.isArray(raw.images)) errors.push('The texture manifest has no images array.');
  if (!Array.isArray(raw.materials)) errors.push('The texture manifest has no materials array.');
  if (errors.length > 0) return { manifest: null, errors, warnings };

  const images: ManifestImage[] = [];
  for (const [index, entry] of (raw.images as unknown[]).entries()) {
    if (!isPlainObject(entry) || typeof entry.file !== 'string' || entry.file.length === 0) {
      // Pushed anyway, as a placeholder: image indices are POSITIONAL, so skipping one would
      // silently shift every slot that pointed past it onto the wrong picture.
      warnings.push(`Image ${index} has no file name; slots that use it are dropped.`);
      images.push({ file: '', mimeType: '', bytes: 0, sourceIndex: index });
      continue;
    }
    images.push({
      file: entry.file,
      mimeType: typeof entry.mimeType === 'string' ? entry.mimeType : '',
      bytes: finiteNumber(entry.bytes) ?? 0,
      sourceIndex: finiteNumber(entry.sourceIndex) ?? index,
    });
  }

  const materials: ManifestMaterial[] = [];
  for (const [index, entry] of (raw.materials as unknown[]).entries()) {
    if (!isPlainObject(entry)) {
      warnings.push(`Material entry ${index} is not an object; dropped.`);
      continue;
    }
    const id = finiteNumber(entry.id);
    const name = typeof entry.name === 'string' ? entry.name : '';
    if (id === undefined && name === '') {
      warnings.push(`Material entry ${index} has neither an id nor a name; dropped.`);
      continue;
    }

    const slots: Partial<Record<TextureSlot, ManifestSlot>> = {};
    const rawSlots = isPlainObject(entry.slots) ? entry.slots : {};
    for (const key of Object.keys(rawSlots)) {
      if (!(TEXTURE_SLOTS as readonly string[]).includes(key)) {
        warnings.push(`Material "${name || id}": slot "${key}" is not supported; ignored.`);
        continue;
      }
      const slot = parseSlot(
        rawSlots[key],
        images.length,
        `Material "${name || id}" slot "${key}"`,
        warnings,
      );
      if (slot && images[slot.image].file !== '') slots[key as TextureSlot] = slot;
    }

    const factors = parseFactors(entry.factors);

    // A material with no slots is still kept when it carries factors: its whole reason to be in
    // the file may be "the stripped GLB says metalness 0, the truth is 1".
    if (Object.keys(slots).length === 0 && !factors) {
      warnings.push(`Material "${name || id}" has no usable texture slots; dropped.`);
      continue;
    }
    const material: ManifestMaterial = { id: id ?? -1, name, slots };
    if (factors) material.factors = factors;
    materials.push(material);
  }

  return {
    manifest: {
      schemaVersion: schemaVersion ?? TEXTURE_MANIFEST_SCHEMA_VERSION,
      generator: typeof raw.generator === 'string' ? raw.generator : '',
      model: typeof raw.model === 'string' ? raw.model : '',
      flipY: false,
      images,
      materials,
      warnings: Array.isArray(raw.warnings)
        ? raw.warnings.filter((w): w is string => typeof w === 'string')
        : [],
    },
    errors,
    warnings,
  };
}
